import * as THREE from 'three';
import { isDebug, settings } from '@/config';
import { createLogger } from '@/utils/logger';
import type { Renderer } from '@/renderer/Renderer';
import type { PerfStats, QualityTier, TickState } from '@/types';

/**
 * Live performance monitor. Samples FPS + renderer.info (draw calls, triangles,
 * geometries, textures, programs) once per second, optionally renders an
 * on-screen HUD (debug mode), logs a periodic console report, and can drive the
 * adaptive quality fallback when enabled.
 */
export class PerformanceMonitor {
  readonly stats: PerfStats = {
    fps: 0,
    ms: 0,
    calls: 0,
    triangles: 0,
    lines: 0,
    points: 0,
    geometries: 0,
    textures: 0,
    programs: 0,
  };

  /** Notified when the adaptive quality tier changes. */
  onQualityChange?: (tier: QualityTier) => void;
  /** While true (a magnet glide / checkpoint travel / video transition / intro is running),
   *  adaptation PAUSES — those moments dip FPS by design, and a tier switch mid-transition IS
   *  the visible flash + quality pop the user reported (2026-07-23). Set per-tick by
   *  Experience; streaks reset so a transition can never bank "bad" samples. */
  adaptSuspended = false;

  private readonly log = createLogger('perf');
  private frames = 0;
  private accumMs = 0;
  private fps = 0;
  private lastReportMs = 0;
  private lastAdaptiveMs = 0;
  private tier: QualityTier = 'high';
  private hud: HTMLElement | null = null;
  /** Consecutive 1s samples below/above the thresholds — tier moves only on a STREAK, and
   *  never again within the cooldown, so one bad second can't flip the whole render config. */
  private badStreak = 0;
  private goodStreak = 0;
  private lastTierChangeMs = -Infinity;

  constructor(private readonly renderer: Renderer) {
    if (isDebug && settings.performance.overlay) this.mountHud();
  }

  update(tick: TickState): void {
    this.frames += 1;
    this.accumMs += tick.delta * 1000;
    if (this.accumMs < 1000) return;

    this.fps = (this.frames * 1000) / this.accumMs;
    this.frames = 0;
    this.accumMs = 0;

    this.sample();
    if (this.hud) this.renderHud();
    this.maybeReport(tick);
    this.maybeAdapt(tick);
  }

  private sample(): void {
    const info = this.renderer.info;
    this.stats.fps = Math.round(this.fps);
    this.stats.ms = this.fps > 0 ? Number((1000 / this.fps).toFixed(2)) : 0;
    this.stats.calls = info.render.calls;
    this.stats.triangles = info.render.triangles;
    this.stats.lines = info.render.lines;
    this.stats.points = info.render.points;
    this.stats.geometries = info.memory.geometries;
    this.stats.textures = info.memory.textures;
    this.stats.programs = info.programs?.length ?? 0;
  }

  private maybeReport(tick: TickState): void {
    if (!isDebug) return;
    if (tick.elapsedMs - this.lastReportMs < settings.performance.reportIntervalMs) return;
    this.lastReportMs = tick.elapsedMs;
    this.log.debug('render report', { ...this.stats });
  }

  private maybeAdapt(tick: TickState): void {
    const cfg = settings.performance.adaptive;
    if (!cfg.enabled) return;
    // Transitions dip FPS on purpose — never judge (or bank) samples taken during one.
    if (this.adaptSuspended) {
      this.badStreak = 0;
      this.goodStreak = 0;
      return;
    }
    if (tick.elapsedMs - this.lastAdaptiveMs < cfg.checkIntervalMs) return;
    this.lastAdaptiveMs = tick.elapsedMs;
    // Post-change cooldown: let the new config actually settle before judging it.
    if (tick.elapsedMs - this.lastTierChangeMs < cfg.cooldownMs) return;

    if (this.fps < cfg.minFps) {
      this.badStreak += 1;
      this.goodStreak = 0;
    } else if (this.fps > cfg.recoverFps) {
      this.goodStreak += 1;
      this.badStreak = 0;
    } else {
      this.badStreak = 0;
      this.goodStreak = 0;
    }

    let next = this.tier;
    if (this.badStreak >= cfg.dropSamples) next = this.tier === 'high' ? 'medium' : 'low';
    else if (this.goodStreak >= cfg.raiseSamples && this.tier !== 'high') {
      next = this.tier === 'low' ? 'medium' : 'high';
    }

    if (next !== this.tier) {
      this.tier = next;
      this.badStreak = 0;
      this.goodStreak = 0;
      this.lastTierChangeMs = tick.elapsedMs;
      this.renderer.applyQualityTier(next);
      this.onQualityChange?.(next);
      this.log.warn(`adaptive quality -> ${next} (${Math.round(this.fps)} fps)`);
    }
  }

  /**
   * Seed the tier the monitor believes it is on, for when something else picks the starting tier
   * (the boot-time device classification — see `Capabilities.classifyTier`).
   *
   * This has to stay in sync or the adaptation logic inverts: `maybeAdapt` steps relative to
   * `this.tier`, so a monitor that thinks it is on `high` while the renderer is actually on `low`
   * would respond to BAD frames by stepping to `medium` — raising quality on a struggling
   * machine — and would never raise from `low` because its `tier !== 'high'` guard reads false.
   */
  setInitialTier(tier: QualityTier): void {
    this.tier = tier;
    this.badStreak = 0;
    this.goodStreak = 0;
  }

  /** The tier currently in force (debug readout). */
  get currentTier(): QualityTier {
    return this.tier;
  }

  /** On-demand full console report (also wired to the debug panel). */
  report(): void {
    this.sample();
    this.log.group('performance report');
    console.table(this.stats);
    this.log.groupEnd();
  }

  private mountHud(): void {
    const el = document.createElement('div');
    // LEFT-MIDDLE, stacked directly above the TIMELINE readout (which is centred at top:50% —
    // see `ScrollReadout`): anchored by its BOTTOM edge 78px above centre, so the pair reads as
    // one left-middle HUD column. Moved from top-left 2026-07-22 — it overlapped the logo there.
    el.style.cssText =
      'position:fixed;top:calc(50% - 78px);left:16px;transform:translateY(-100%);' +
      'z-index:9999;pointer-events:none;white-space:pre;' +
      'font:11px/1.5 ui-monospace,Menlo,Consolas,monospace;color:#cdd6ff;' +
      'background:rgba(10,10,14,.72);padding:8px 10px;border:1px solid rgba(139,156,255,.25);' +
      'border-radius:6px;backdrop-filter:blur(4px)';
    document.body.appendChild(el);
    this.hud = el;
  }

  private renderHud(): void {
    if (!this.hud) return;
    const s = this.stats;
    const gl = this.renderer.instance;
    const size = gl.getSize(this._hudVec);
    const dpr = gl.getPixelRatio();
    this.hud.textContent =
      `FPS    ${s.fps}\n` +
      `MS     ${s.ms}\n` +
      `RES    ${Math.round(size.x)}×${Math.round(size.y)} @${dpr.toFixed(2)}x\n` +
      `SCALE  ${this.renderer.getPixelRatioScale().toFixed(2)}\n` +
      `CALLS  ${s.calls}\n` +
      `TRIS   ${s.triangles.toLocaleString()}\n` +
      `GEO    ${s.geometries}\n` +
      `TEX    ${s.textures}\n` +
      `PROG   ${s.programs}\n` +
      `TIER   ${this.tier}`;
  }

  private readonly _hudVec = new THREE.Vector2();

  dispose(): void {
    this.hud?.remove();
    this.hud = null;
  }
}
